import { CameraOutlined, DeleteOutlined, PictureOutlined } from '@ant-design/icons';
import type { ColorPickerProps } from 'antd';
import { Button, ColorPicker, Form, Input, Modal, Select, Switch, Upload } from 'antd';
import type { UploadChangeParam } from 'antd/es/upload';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { PreviewImage } from '@/components/PreviewImage/PreviewImage';
import { RichTextEditor } from '@/components/RichTextEditor/RichTextEditor';
import { BRAND } from '@/constants/brand';
import { COLOR_SWATCH_PRESETS, normalizeHexColor } from '@/constants/colorSwatches';
import { useNotify } from '@/hooks/useNotify';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useMutationReset } from '@/hooks/useMutationReset';
import { useGetAttributesQuery } from '@/modules/attributes/attributesApi';
import {
  useCreateAttributeValueMutation,
  useGetAttributeValuesQuery,
  useUpdateAttributeValueMutation,
  useUploadAttributeValueImageMutation,
} from '@/modules/attribute-values/attributeValuesApi';
import type {
  AttributeValue,
  AttributeValueInfoTooltip,
  CreateAttributeValuePayload,
} from '@/types/attribute-value';
import { resolveMutationResult } from '@/utils/apiResponse';
import { isColorAttribute, isFormatAccessAttribute, isImprintAttribute } from '@/utils/attribute';
import { getAttributeValueImageUrl } from '@/utils/media';
import categoryStyles from '../../categories/components/CategoryFormModal.module.scss';
import styles from './AttributeValueFormModal.module.scss';

interface AttributeValueFormModalProps {
  open: boolean;
  attributeValue?: AttributeValue | null;
  defaultAttributeId?: string;
  onClose: () => void;
}

type AttributeValueFormValues = CreateAttributeValuePayload & {
  colorCode?: string;
  infoTooltips: AttributeValueInfoTooltip[];
};

const COLOR_PICKER_PRESETS: ColorPickerProps['presets'] = [
  {
    label: 'Palette',
    colors: COLOR_SWATCH_PRESETS.map((preset) => preset.color),
  },
];

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const MAX_SIZE_BYTES = 5 * 1024 * 1024;

export function AttributeValueFormModal({
  open,
  attributeValue,
  defaultAttributeId,
  onClose,
}: AttributeValueFormModalProps) {
  const notify = useNotify();
  const isEdit = Boolean(attributeValue);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageCleared, setImageCleared] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>();
  const { data: attributesResult } = useGetAttributesQuery({
    page: 1,
    limit: 100,
    sortBy: 'name',
    sortOrder: 'asc',
  });
  const attributes = useMemo(() => attributesResult?.results ?? [], [attributesResult?.results]);
  const [createAttributeValue, createState] = useCreateAttributeValueMutation();
  const [updateAttributeValue, updateState] = useUpdateAttributeValueMutation();
  const [uploadAttributeValueImage, uploadState] = useUploadAttributeValueImageMutation();
  const isLoading =
    createState.isLoading || updateState.isLoading || uploadState.isLoading;
  useGlobalLoader(isLoading);

  const attributeOptions = useMemo(
    () => attributes.map((attribute) => ({ label: attribute.name, value: attribute.id })),
    [attributes],
  );

  const { control, handleSubmit, reset, setValue } = useForm<AttributeValueFormValues>({
    defaultValues: {
      attributeId: '',
      name: '',
      subtitle: '',
      description: '',
      mainDescription: '',
      whatsInside: '',
      editorialStandard: '',
      editorialSources: '',
      colorCode: '',
      infoTooltipEnabled: false,
      infoTooltipHtml: '',
      infoTooltips: [],
      isActive: true,
    },
  });

  const selectedAttributeId = useWatch({ control, name: 'attributeId' });
  const infoTooltipsWatch = useWatch({ control, name: 'infoTooltips' });
  const { fields: infoTooltipFields } = useFieldArray({ control, name: 'infoTooltips' });
  const selectedAttribute = useMemo(
    () => attributes.find((attribute) => attribute.id === selectedAttributeId) ?? null,
    [attributes, selectedAttributeId],
  );
  const showColorPicker = isColorAttribute(selectedAttribute);
  const showImprintFields = isImprintAttribute(selectedAttribute);
  const showFormatInfoFields = isFormatAccessAttribute(selectedAttribute);
  const imprintAttributeId = useMemo(
    () => attributes.find((attribute) => isImprintAttribute(attribute))?.id,
    [attributes],
  );
  const { data: imprintValuesResult } = useGetAttributeValuesQuery(
    {
      attributeId: imprintAttributeId,
      page: 1,
      limit: 100,
      sortBy: 'name',
      sortOrder: 'asc',
    },
    { skip: !open || !imprintAttributeId || !showFormatInfoFields },
  );
  const imprintOptions = useMemo(
    () => imprintValuesResult?.results ?? [],
    [imprintValuesResult?.results],
  );

  useMutationReset(createState.reset);
  useMutationReset(updateState.reset);
  useMutationReset(uploadState.reset);

  const existingImageUrl = useMemo(
    () => getAttributeValueImageUrl(attributeValue?.image),
    [attributeValue?.image],
  );

  useEffect(() => {
    if (!open) return;

    const nextAttributeId =
      attributeValue?.attributeId ?? defaultAttributeId ?? attributes[0]?.id ?? '';
    const nextAttribute = attributes.find((item) => item.id === nextAttributeId) ?? null;
    const saved = new Map(
      (attributeValue?.infoTooltips ?? []).map((item) => [item.imprintId, item]),
    );

    reset({
      attributeId: nextAttributeId,
      name: attributeValue?.name ?? '',
      subtitle: attributeValue?.subtitle ?? '',
      description: attributeValue?.description ?? '',
      mainDescription: attributeValue?.mainDescription ?? '',
      whatsInside: attributeValue?.whatsInside ?? '',
      editorialStandard: attributeValue?.editorialStandard ?? '',
      editorialSources: attributeValue?.editorialSources ?? '',
      colorCode: attributeValue?.colorCode ? normalizeHexColor(attributeValue.colorCode) : '',
      infoTooltipEnabled: false,
      infoTooltipHtml: '',
      infoTooltips: isFormatAccessAttribute(nextAttribute)
        ? imprintOptions.map((imprint) => ({
            imprintId: imprint.id,
            enabled: saved.get(imprint.id)?.enabled ?? false,
            html: saved.get(imprint.id)?.html ?? '',
          }))
        : [],
      isActive: attributeValue?.isActive ?? true,
    });
    setImageFile(null);
    setImageCleared(false);
    setPreviewUrl(existingImageUrl);
  }, [
    attributeValue,
    attributes,
    defaultAttributeId,
    existingImageUrl,
    imprintOptions,
    open,
    reset,
  ]);

  useEffect(() => {
    if (!open || showColorPicker) return;
    setValue('colorCode', '');
  }, [open, setValue, showColorPicker]);

  useEffect(() => {
    if (!open || showImprintFields) return;
    setValue('subtitle', '');
    setValue('description', '');
    setValue('mainDescription', '');
    setValue('whatsInside', '');
    setValue('editorialStandard', '');
    setValue('editorialSources', '');
    setImageFile(null);
    setImageCleared(false);
    setPreviewUrl(undefined);
  }, [open, setValue, showImprintFields]);

  useEffect(() => {
    if (!open || !showFormatInfoFields || !imprintOptions.length) return;
    const current = infoTooltipsWatch ?? [];
    const sameIds =
      current.length === imprintOptions.length &&
      current.every((row, index) => row.imprintId === imprintOptions[index]?.id);
    if (sameIds) return;

    const saved = new Map(
      (attributeValue?.infoTooltips ?? []).map((item) => [item.imprintId, item]),
    );
    setValue(
      'infoTooltips',
      imprintOptions.map((imprint) => ({
        imprintId: imprint.id,
        enabled: saved.get(imprint.id)?.enabled ?? false,
        html: saved.get(imprint.id)?.html ?? '',
      })),
    );
  }, [
    attributeValue?.infoTooltips,
    imprintOptions,
    infoTooltipsWatch,
    open,
    setValue,
    showFormatInfoFields,
  ]);

  const handleImageChange = (info: UploadChangeParam) => {
    const file = info.file.originFileObj ?? info.file;
    if (!file || !(file instanceof File)) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      notify.error('Please upload a PNG, JPG, WEBP, or GIF image.');
      return;
    }

    if (file.size > MAX_SIZE_BYTES) {
      notify.error('Image must be 5MB or smaller.');
      return;
    }

    setImageFile(file);
    setImageCleared(false);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImageCleared(true);
    setPreviewUrl(undefined);
  };

  const uploadImageIfNeeded = async (): Promise<string | undefined> => {
    if (imageFile) {
      const formData = new FormData();
      formData.append('image', imageFile);
      const result = await resolveMutationResult<{ image: string }>(
        uploadAttributeValueImage(formData),
      );

      if (!result.ok) {
        notify.error(result.message);
        return undefined;
      }

      return result.data.image;
    }

    if (imageCleared) {
      return undefined;
    }

    return attributeValue?.image;
  };

  const onSubmit = async (values: AttributeValueFormValues) => {
    let image = '';

    if (showImprintFields) {
      const uploadedImage = await uploadImageIfNeeded();
      if (!uploadedImage) {
        notify.error('Image is required.');
        return;
      }
      image = uploadedImage;
    }

    const colorCode =
      showColorPicker && values.colorCode ? normalizeHexColor(values.colorCode) : undefined;

    const payload: CreateAttributeValuePayload = {
      attributeId: values.attributeId,
      name: values.name.trim(),
      subtitle: showImprintFields ? values.subtitle?.trim() ?? '' : '',
      description: showImprintFields ? values.description?.trim() ?? '' : '',
      mainDescription: showImprintFields ? values.mainDescription?.trim() ?? '' : '',
      whatsInside: showImprintFields ? values.whatsInside?.trim() ?? '' : '',
      editorialStandard: showImprintFields ? values.editorialStandard?.trim() ?? '' : '',
      editorialSources: showImprintFields ? values.editorialSources?.trim() ?? '' : '',
      image: showImprintFields ? image : '',
      infoTooltipEnabled: false,
      infoTooltipHtml: '',
      infoTooltips: showFormatInfoFields
        ? (values.infoTooltips ?? []).map((item) => ({
            imprintId: item.imprintId,
            enabled: Boolean(item.enabled),
            html: item.html?.trim() ?? '',
          }))
        : [],
      isActive: values.isActive,
      ...(colorCode ? { colorCode } : {}),
    };

    if (isEdit) {
      if (!attributeValue?.id) return;

      const result = await resolveMutationResult(
        updateAttributeValue({ id: attributeValue.id, body: payload }),
      );
      if (result.ok) {
        notify.success(result.message ?? 'Sub attribute updated successfully.');
        onClose();
        return;
      }

      notify.error(result.message);
      return;
    }

    const result = await resolveMutationResult(createAttributeValue(payload));

    if (result.ok) {
      notify.success(result.message ?? 'Sub attribute created successfully.');
      onClose();
      return;
    }

    notify.error(result.message);
  };

  return (
    <Modal
      title={isEdit ? 'Edit sub attribute' : 'Add sub attribute'}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
      width={showImprintFields || showFormatInfoFields ? 760 : 560}
    >
      <Form layout="vertical" className={categoryStyles.form} onFinish={handleSubmit(onSubmit)}>
        <Controller
          name="attributeId"
          control={control}
          rules={{ required: 'Attribute is required' }}
          render={({ field, fieldState }) => (
            <Form.Item
              label="Attribute"
              required
              validateStatus={fieldState.error ? 'error' : ''}
              help={fieldState.error?.message}
            >
              <Select
                {...field}
                options={attributeOptions}
                placeholder="Select attribute"
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          )}
        />

        {showImprintFields ? (
          <Controller
            name="subtitle"
            control={control}
            render={({ field, fieldState }) => (
              <Form.Item
                label="Subtitle"
                validateStatus={fieldState.error ? 'error' : ''}
                help={fieldState.error?.message}
              >
                <Input {...field} placeholder="e.g. Election Reference Series" />
              </Form.Item>
            )}
          />
        ) : null}

        {showColorPicker ? (
          <Controller
            name="colorCode"
            control={control}
            rules={{ required: 'Color is required' }}
            render={({ field: colorField, fieldState: colorState }) => (
              <Controller
                name="name"
                control={control}
                rules={{ required: 'Title is required' }}
                render={({ field: nameField, fieldState: nameState }) => (
                  <Form.Item
                    label="Title"
                    required
                    validateStatus={colorState.error || nameState.error ? 'error' : ''}
                    help={colorState.error?.message || nameState.error?.message}
                  >
                    <div className={styles.valueWithColor}>
                      <ColorPicker
                        value={colorField.value || '#115099'}
                        onChange={(_, hex) => colorField.onChange(normalizeHexColor(hex))}
                        onChangeComplete={(color) =>
                          colorField.onChange(normalizeHexColor(color.toHexString()))
                        }
                        format="hex"
                        disabledAlpha
                        presets={COLOR_PICKER_PRESETS}
                        className={styles.colorPicker}
                      />
                      <Input
                        {...nameField}
                        className={styles.valueInput}
                        placeholder="e.g. Print"
                      />
                    </div>
                  </Form.Item>
                )}
              />
            )}
          />
        ) : (
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Title is required' }}
            render={({ field, fieldState }) => (
              <Form.Item
                label="Title"
                required
                validateStatus={fieldState.error ? 'error' : ''}
                help={fieldState.error?.message}
              >
                <Input {...field} placeholder="e.g. Print, eFlipBook" />
              </Form.Item>
            )}
          />
        )}

        {showImprintFields ? (
          <Controller
            name="description"
            control={control}
            render={({ field, fieldState }) => (
              <Form.Item
                label="Short description"
                validateStatus={fieldState.error ? 'error' : ''}
                help={fieldState.error?.message}
              >
                <Input.TextArea
                  {...field}
                  rows={2}
                  placeholder="Brief line shown with the imprint title"
                />
              </Form.Item>
            )}
          />
        ) : null}

        {showImprintFields ? (
          <Controller
            name="mainDescription"
            control={control}
            render={({ field, fieldState }) => (
              <Form.Item
                label="Main description"
                validateStatus={fieldState.error ? 'error' : ''}
                help={fieldState.error?.message}
              >
                <RichTextEditor
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  placeholder="Full catalogue description for this imprint"
                />
              </Form.Item>
            )}
          />
        ) : null}

        {showImprintFields ? (
          <Controller
            name="whatsInside"
            control={control}
            render={({ field, fieldState }) => (
              <Form.Item
                label="What's inside"
                validateStatus={fieldState.error ? 'error' : ''}
                help={fieldState.error?.message}
              >
                <RichTextEditor
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  placeholder="Use Heading 3 for each chapter title, followed by a paragraph"
                />
              </Form.Item>
            )}
          />
        ) : null}

        {showImprintFields ? (
          <Controller
            name="editorialStandard"
            control={control}
            render={({ field, fieldState }) => (
              <Form.Item
                label="On the editorial standard"
                validateStatus={fieldState.error ? 'error' : ''}
                help={fieldState.error?.message}
              >
                <RichTextEditor
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  placeholder="Heading and paragraphs describing how this imprint is compiled"
                />
              </Form.Item>
            )}
          />
        ) : null}

        {showImprintFields ? (
          <Controller
            name="editorialSources"
            control={control}
            render={({ field, fieldState }) => (
              <Form.Item
                label="Contents at a glance"
                validateStatus={fieldState.error ? 'error' : ''}
                help={fieldState.error?.message || 'Use a numbered or bullet list — chapter numbers (I, II, III…) are shown automatically on the product page.'}
              >
                <RichTextEditor
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  placeholder="List each chapter (e.g. Constituency at a Glance, Maps, Demographic Profile…)"
                />
              </Form.Item>
            )}
          />
        ) : null}

        {showImprintFields ? (
          <Form.Item label="Image" required={!isEdit} className={categoryStyles.imageField}>
          <div className={categoryStyles.imagePicker}>
            <Upload
              accept={ACCEPTED_TYPES.join(',')}
              showUploadList={false}
              beforeUpload={() => false}
              onChange={handleImageChange}
              disabled={isLoading}
            >
              <button type="button" className={categoryStyles.imageThumb}>
                {previewUrl ? (
                  <PreviewImage
                    src={previewUrl}
                    alt="Sub attribute preview"
                    fill
                    className={categoryStyles.imagePreview}
                    stopPropagation
                  />
                ) : (
                  <span className={categoryStyles.imagePlaceholder}>
                    <PictureOutlined />
                  </span>
                )}
                <span className={categoryStyles.imageOverlay}>
                  <CameraOutlined />
                </span>
              </button>
            </Upload>

            <div className={categoryStyles.imageMeta}>
              <p className={categoryStyles.imageTitle}>
                {previewUrl ? 'Image selected' : 'Upload image'}
              </p>
              <p className={categoryStyles.imageHint}>PNG, JPG, WEBP or GIF · Max 5MB</p>
              {previewUrl ? (
                <div className={categoryStyles.imageActions}>
                  <Upload
                    accept={ACCEPTED_TYPES.join(',')}
                    showUploadList={false}
                    beforeUpload={() => false}
                    onChange={handleImageChange}
                    disabled={isLoading}
                  >
                    <Button size="small" type="default">
                      Change
                    </Button>
                  </Upload>
                  <Button
                    size="small"
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={handleRemoveImage}
                    disabled={isLoading}
                  >
                    Remove
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        </Form.Item>
        ) : null}

        {showFormatInfoFields ? (
          <div className={styles.imprintTooltipList}>
            <p className={styles.imprintTooltipIntro}>
              One info box per imprint. Hover text on the product page uses the product’s imprint.
            </p>
            {infoTooltipFields.length ? (
              infoTooltipFields.map((field, index) => {
                const imprintName =
                  imprintOptions.find((imprint) => imprint.id === field.imprintId)?.name ??
                  'Imprint';
                const enabled = Boolean(infoTooltipsWatch?.[index]?.enabled);
                return (
                  <div key={field.id} className={styles.imprintTooltip}>
                    <Controller
                      name={`infoTooltips.${index}.imprintId`}
                      control={control}
                      render={({ field: idField }) => <input type="hidden" {...idField} />}
                    />
                    <div className={styles.imprintTooltipHead}>
                      <span className={styles.imprintTooltipTitle}>{imprintName}</span>
                      <Controller
                        name={`infoTooltips.${index}.enabled`}
                        control={control}
                        render={({ field: enabledField }) => (
                          <Switch
                            checked={enabledField.value}
                            onChange={enabledField.onChange}
                            checkedChildren="On"
                            unCheckedChildren="Off"
                            style={{ background: enabledField.value ? BRAND.primary : undefined }}
                          />
                        )}
                      />
                    </div>
                    {enabled ? (
                      <Controller
                        name={`infoTooltips.${index}.html`}
                        control={control}
                        render={({ field: htmlField, fieldState }) => (
                          <Form.Item
                            label="Info content"
                            validateStatus={fieldState.error ? 'error' : ''}
                            help={
                              fieldState.error?.message ||
                              'Text and links. Links open in a new tab (page or PDF).'
                            }
                          >
                            <RichTextEditor
                              value={htmlField.value ?? ''}
                              onChange={htmlField.onChange}
                              height={140}
                              placeholder={`Info for ${imprintName}`}
                            />
                          </Form.Item>
                        )}
                      />
                    ) : (
                      <p className={styles.imprintTooltipOff}>
                        Icon hidden for this imprint. Turn on to add hover text.
                      </p>
                    )}
                  </div>
                );
              })
            ) : (
              <p className={styles.imprintTooltipOff}>
                No active imprints found. Add Imprint values first.
              </p>
            )}
          </div>
        ) : null}

        <Controller
          name="isActive"
          control={control}
          render={({ field }) => (
            <Form.Item label="Status" className={categoryStyles.statusField}>
              <Switch
                checked={field.value}
                onChange={field.onChange}
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                style={{ background: field.value ? BRAND.primary : undefined }}
              />
            </Form.Item>
          )}
        />

        <div className={categoryStyles.actions}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {isEdit ? 'Save changes' : 'Create sub attribute'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
